// Pure derivations for the Helmet Impact tab of the Team and Player pages. The tab counts in the browser from
// `GET /api/helmet-impacts` `impacts`; it does not read `daily`.

/** @typedef {"Front" | "Top" | "Left" | "Right" | "Back"} HelmetLocation */
/** @typedef {"low" | "medium" | "high"} HelmetBucket */

/** @type {HelmetLocation[]} */
export const HELMET_LOCATIONS = ["Front", "Top", "Left", "Right", "Back"];
/** @type {HelmetBucket[]} */
export const HELMET_BUCKETS = ["low", "medium", "high"];
export const BUCKET_LABEL = { low: "Low", medium: "Medium", high: "High" };
/** @type {Record<HelmetLocation, "front" | "top" | "left" | "right" | "back">} */
export const LOCATION_TO_ZONE = { Front: "front", Top: "top", Left: "left", Right: "right", Back: "back" };

export const MEDIUM_LOAD = 42;
export const HIGH_LOAD = 100;

/** The impact time in the time zone of the browser, on a 12-hour clock: 1:42:05 PM. */
export const impactTimeLabel = (impact) =>
  new Date(impact.impact_time_utc).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit" });

export const bucketForLoad = (load) => (load < MEDIUM_LOAD ? "low" : load < HIGH_LOAD ? "medium" : "high");

export function groupByDay(impacts) {
  const days = new Map();
  for (const impact of impacts) {
    if (!days.has(impact.date)) days.set(impact.date, []);
    days.get(impact.date).push(impact);
  }
  return days;
}

/** The days with data inside the range, oldest first. */
export const daysInRange = (days, startDate, endDate) =>
  [...days.keys()].filter((d) => d >= startDate && d <= endDate).sort();

export function dailyCounts(days, dayList) {
  return dayList.map((day) => {
    const impacts = days.get(day);
    const counts = Object.fromEntries(HELMET_BUCKETS.map((b) => [b, impacts.filter((i) => i.bucket === b).length]));
    return { day, ...counts, total: impacts.length, alerts: impacts.filter((i) => i.is_alert).length };
  });
}

/** #fdb863 to #e31a1c, so one hit never looks like a zone with no hits (white). */
export function heatColor(share) {
  const low = [0xfd, 0xb8, 0x63];
  const high = [0xe3, 0x1a, 0x1c];
  return `#${low.map((c, k) => Math.round(c + (high[k] - c) * share).toString(16).padStart(2, "0")).join("")}`;
}

export function zoneCounts(impacts) {
  const counts = Object.fromEntries(HELMET_LOCATIONS.map((l) => [l, impacts.filter((i) => i.location === l).length]));
  const most = Math.max(1, ...Object.values(counts));
  const total = impacts.length;
  return HELMET_LOCATIONS.map((location) => ({
    location,
    zone: LOCATION_TO_ZONE[location],
    count: counts[location],
    share: total ? counts[location] / total : 0,
    color: counts[location] ? heatColor(counts[location] / most) : null,
  }));
}

const meanSkipNull = (impacts, key) => {
  const values = impacts.map((i) => i[key]).filter((v) => v != null);
  return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
};

/** A group row. The load per impact is the sum of load over the impact count, not an average of player values. */
export function groupStats(impacts) {
  const load = impacts.reduce((sum, i) => sum + i.load, 0);
  return {
    impacts: impacts.length,
    load,
    loadPerImpact: impacts.length ? load / impacts.length : null,
    linearAcceleration: meanSkipNull(impacts, "linear_acceleration"),
    peakHitSp: meanSkipNull(impacts, "peak_hit_sp"),
    alerts: impacts.filter((i) => i.is_alert).length,
    buckets: Object.fromEntries(HELMET_BUCKETS.map((b) => [b, impacts.filter((i) => i.bucket === b).length])),
    locations: Object.fromEntries(HELMET_LOCATIONS.map((l) => [l, impacts.filter((i) => i.location === l).length])),
  };
}

export const passesTableFilters = (impact, { alertsOnly, locations }) =>
  (!alertsOnly || impact.is_alert) && (!locations?.length || locations.includes(impact.location));

/**
 * The table tree: day, then player, then impact. Days go from newest to oldest, players by load (highest first),
 * impacts by local time.
 */
/**
 * @param {any[]} impacts
 * @param {{ startDate: string, endDate: string, alertsOnly?: boolean, locations?: string[] }} options
 */
export function tableTree(impacts, { startDate, endDate, alertsOnly = false, locations = [] }) {
  const kept = impacts.filter((i) => i.date >= startDate && i.date <= endDate && passesTableFilters(i, { alertsOnly, locations }));
  const days = groupByDay(kept);
  return [...days.keys()].sort().reverse().map((day) => {
    const dayImpacts = days.get(day);
    const players = new Map();
    for (const impact of dayImpacts) {
      if (!players.has(impact.player_id)) players.set(impact.player_id, []);
      players.get(impact.player_id).push(impact);
    }
    const playerRows = [...players.entries()]
      .map(([playerId, list]) => ({
        playerId,
        stats: groupStats(list),
        impacts: [...list].sort((a, b) => a.impact_time_local.localeCompare(b.impact_time_local)),
      }))
      .sort((a, b) => b.stats.load - a.stats.load);
    return { day, stats: groupStats(dayImpacts), players: playerRows };
  });
}

/** After a delete empties the selected day: the nearest day with data, the later one on a tie. */
export function nearestDay(dayList, day) {
  if (!dayList.length) return null;
  if (dayList.includes(day)) return day;
  const distance = (d) => Math.abs(new Date(`${d}T00:00:00Z`) - new Date(`${day}T00:00:00Z`));
  return [...dayList].sort((a, b) => distance(a) - distance(b) || b.localeCompare(a))[0];
}

/** The PATCH body from the edit form. Severity follows the load; empty optional numbers send null. */
export function editBody({ load, linearAcceleration, peakHitSp, location, isAlert }) {
  if (load === null || load === undefined || load === "") return null;
  const optional = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
  return {
    load: Number(load),
    bucket: bucketForLoad(Number(load)),
    linear_acceleration: optional(linearAcceleration),
    peak_hit_sp: optional(peakHitSp),
    location,
    is_alert: !!isAlert,
  };
}
